/**
 * Small drawings of what each overlay option looks like.
 *
 * ## Why not just frame the real thing four times
 *
 * Because four live iframes in one dialog is four polling loops, four sets of
 * artwork requests and a modal that takes a second to settle - to help with a
 * choice a host makes once. These are static shapes: enough to tell a spinning
 * record from a waveform at a glance, and honest about being a sketch. The one
 * preview at the bottom of the dialog is the real page.
 *
 * They are deliberately not pixel-accurate. A thumbnail that tried to be would
 * be a second implementation of `overlay.css` in React, which is a thing that
 * drifts.
 */

const ART = 'linear-gradient(135deg,#f0559a,#f2a13f)';

export function OverlayThumb({
  kind,
  style,
  orientation = 'landscape',
  theme = 'dark',
  format = 'card',
}: {
  kind: 'now' | 'list';
  style?: string;
  orientation?: string;
  theme?: string;
  format?: string;
}) {
  return (
    /*
     * A stand-in for video behind the card.
     *
     * On a flat dark square the minimal theme - which is the absence of a card -
     * looks identical to the dark theme, which is the one thing these pictures
     * exist to distinguish. Something with tone in it makes the plate visible
     * as a plate, and says what the overlay sits on.
     */
    <div
      aria-hidden="true"
      className="flex h-[74px] w-full items-center justify-center overflow-hidden rounded-[6px] p-2"
      style={{
        background:
          'radial-gradient(120% 120% at 20% 0%, #33324e 0%, #1b1b2c 45%, #0d0d18 100%)',
      }}
    >
      {kind === 'now' ? (
        <NowThumb style={style ?? 'glass'} orientation={orientation} />
      ) : (
        <ListThumb theme={theme} format={format} />
      )}
    </div>
  );
}

function NowThumb({ style, orientation }: { style: string; orientation: string }) {
  const portrait = orientation === 'portrait';

  const art =
    style === 'vinyl' ? (
      <div className="relative flex items-center justify-center rounded-full bg-[#15151f]"
           style={{ width: portrait ? 34 : 26, height: portrait ? 34 : 26 }}>
        <div
          className="rounded-full"
          style={{ width: '54%', height: '54%', background: ART }}
        />
      </div>
    ) : (
      <div
        className="rounded-[3px]"
        style={{ width: portrait ? 34 : 26, height: portrait ? 34 : 26, background: ART }}
      />
    );

  const lines = (
    <div className={`flex flex-col gap-1 ${portrait ? 'items-stretch' : ''}`}>
      <Line width={portrait ? 40 : 44} tone="bright" />
      <Line width={portrait ? 26 : 28} />
      {style === 'waveform' ? <Wave width={portrait ? 40 : 48} /> : <Line width={portrait ? 40 : 48} thin />}
    </div>
  );

  return (
    <div
      className={`flex items-center gap-2 rounded-[5px] bg-[#141422] p-1.5 ${
        portrait ? 'flex-col' : ''
      }`}
    >
      {style === 'spectrum' && <Eq />}
      {art}
      {lines}
      {style === 'spectrum' && <Eq />}
    </div>
  );
}

function ListThumb({ theme, format }: { theme: string; format: string }) {
  const plate =
    theme === 'light' ? 'bg-[#eeeef4]' : theme === 'minimal' ? 'bg-transparent' : 'bg-[#141422]';
  const tone = theme === 'light' ? '#8f8fa6' : '#4a4a68';

  if (format === 'ticker') {
    return (
      <div className={`flex w-full items-center gap-2 rounded-[4px] px-1.5 py-2 ${plate}`}>
        <div className="h-1.5 w-1.5 flex-none rounded-full bg-[#33e58a]" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-none items-center gap-1">
            <div className="h-3 w-3 rounded-[2px]" style={{ background: ART }} />
            <div className="h-1 rounded-full" style={{ width: 18 - i * 3, background: tone }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex w-[86px] flex-col gap-1.5 rounded-[4px] p-1.5 ${plate}`}>
      <div className="flex items-center gap-1">
        <div className="h-1 w-1 rounded-full bg-[#33e58a]" />
        <div className="h-[3px] w-8 rounded-full bg-[#33e58a] opacity-70" />
      </div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-1">
          <div className="h-3 w-3 flex-none rounded-[2px]" style={{ background: ART }} />
          <div className="flex flex-col gap-[2px]">
            <div
              className="h-[3px] rounded-full"
              style={{ width: 42 - i * 4, background: i === 0 ? '#33e58a' : tone }}
            />
            <div className="h-[2px] rounded-full" style={{ width: 24, background: tone, opacity: 0.6 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Line({ width, tone, thin }: { width: number; tone?: string; thin?: boolean }) {
  return (
    <div
      className="rounded-full"
      style={{
        width,
        height: thin ? 2 : 3,
        background: tone === 'bright' ? '#c9c9dd' : '#4a4a68',
      }}
    />
  );
}

function Wave({ width }: { width: number }) {
  return (
    <div className="flex items-center gap-[1.5px]" style={{ width, height: 8 }}>
      {Array.from({ length: 14 }, (_, i) => (
        <div
          key={i}
          className="w-[1.5px] flex-none rounded-full"
          style={{
            height: `${28 + Math.abs(((i * 37) % 21) - 10) * 7}%`,
            background: i < 6 ? '#33e58a' : '#3a3a58',
          }}
        />
      ))}
    </div>
  );
}

function Eq() {
  return (
    <div className="flex items-center gap-[1.5px]" style={{ height: 22 }}>
      {[60, 90, 45, 75].map((height, i) => (
        <div
          key={i}
          className="w-[1.5px] rounded-full bg-[#33e58a] opacity-80"
          style={{ height: `${height}%` }}
        />
      ))}
    </div>
  );
}
